import React, { Fragment } from 'react';
import {
	styled,
	Input,
	Button,
	Tooltip,
	Checkbox,
	Select,
	ButtonGroup,
	WholeIcon,
	HalfIcon
} from "@lib/components";
import { FaMinusCircle, FaArrowsAlt, FaCopy } from "react-icons/fa";
import { arrayMove } from "react-sortable-hoc";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { getIndexById, sortByName, MenuListSortDirection } from "../utils/sortLists";
import { MenuSortDirectionButtons } from "./menu-sort-direction-buttons";
import { cloneDeepSafe } from "@lib/common";
import shortid from "shortid";
import { inject, observer } from "mobx-react";
import { MobxComponent } from "../../../../../../mobx/components";
import _ from "lodash"
const autobind = require("class-autobind").default;

let wpos_status: boolean = false;//

type ItemType = T.Schema.Restaurant.Menu.RestaurantOptionSetOption;
type ListType = ItemType[];
type HandleChange = (
	i: number,
	key: keyof ItemType,
	value: string | number | null | { [key: string]: string | number | T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices }
) => void;
type HandleSetDefault = (i: number, quantity: 1 | 0) => void;
type HandleByIndex = (i: number) => void;
interface Props {
	values: T.Schema.Restaurant.Menu.RestaurantOptionSet;
	option_sets: T.Schema.Restaurant.Menu.RestaurantOptionSet[];
	setFieldValue: (key: string, value: any) => void;
	defaultEnabled?: boolean;
	restaurantStock: T.Schema.RestaurantMenuStock.Schema;
	setRestaurantStock: (stock: T.Schema.RestaurantMenuStock.Schema) => void;
}
interface TableDataProps {
	items: ListType;
	sourceOptions: ListType;
	sortDirection: MenuListSortDirection;
	change: HandleChange;
	remove: HandleByIndex;
	copy: HandleByIndex;
	setDefault: HandleSetDefault;
	variablePriceOptionSet: T.Schema.Restaurant.Menu.RestaurantOptionSet | null;
	showVariablePrice: boolean;
	defaultEnabled?: boolean;
	restaurantStock: T.Schema.RestaurantMenuStock.Schema;
	setRestaurantStock: (stock: T.Schema.RestaurantMenuStock.Schema) => void;
	usingPoints?: boolean;
	selectedOptionId?: string;
	currencySymbol?: string;
}

const Table = styled.table`
  background: white;
  thead {
    th {
      white-space: nowrap;
    }
  }
  td {
    vertical-align: middle;
  }
`;
const rowDataProps = { className: "border-white-20 p-lr-1 p-tb-2" };

const isPointPrices = (price: T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPrices, pointType: string) =>
  typeof price === "object" && price !== null && pointType in price;
@observer
class TableData extends React.Component<TableDataProps> {
	render() {
		const {
			items,
			sourceOptions,
			sortDirection,
			change,
			remove,
			copy,
			setDefault,
			defaultEnabled,
			variablePriceOptionSet,
			showVariablePrice,
			restaurantStock,
			setRestaurantStock,
			usingPoints,
			selectedOptionId,
			currencySymbol,
		} = this.props;

		return (
			<>
				{items.map((item) => {
					const i = getIndexById(sourceOptions, item._id);
					if (i === -1) {
						return null;
					}
					const stock =
						typeof restaurantStock.option_set_options[item._id] === "number"
							? restaurantStock.option_set_options[item._id]
							: "";
					// v1 price shapes only: string | number (standard) or { whole, half } point prices.
					type PointPrices = T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices;
					type PointPricesPatch = { [K in keyof PointPrices]?: Partial<PointPrices[K]> };
					const pointPrice: PointPricesPatch =
						typeof item.price === "object" && item.price !== null ? item.price : {};
					const standardDisplayPrice =
						typeof item.price === "object" && item.price !== null ? "" : (item.price ?? "");
					const updatePointPrice = (newValue: PointPricesPatch) => {
						const price = cloneDeepSafe(typeof item.price === "object" && item.price !== null ? item.price : {});
						_.merge(price, newValue);
						change(i, "price", price);
					};
					const row = (dragHandleProps?: React.HTMLAttributes<HTMLElement> | null, innerRef?: React.Ref<HTMLTableRowElement>, draggableProps?: React.HTMLAttributes<HTMLTableRowElement>) => (
						<tr
							ref={innerRef}
							className="bg-white border-white-20"
							{...draggableProps}
						>
							<td
								className="border-white-20 text-center"
								style={sortDirection ? { opacity: 0.35, cursor: "not-allowed" } : { cursor: "grab" }}
								{...(sortDirection ? {} : dragHandleProps || {})}
							>
								<Tooltip
									text={
										sortDirection
											? "Turn off alphabetical sort to reorder"
											: "Drag to reorder"
									}
									width={sortDirection ? 220 : 100}
									position="top"
								>
									<FaArrowsAlt />
								</Tooltip>
							</td>

							<td className="border-white-20">
								<div className="flex-center">
									<Input
										type="text"
										className="no-round no-border"
										value={item.name}
										placeholder={"Name"}
										required={true}
										onChange={(e: any) => change(i, "name", e.target.value)}
									/>
								</div>
							</td>
							<td className="border-white-20">
								<div className="flex-center">
									<Input
										type="text"
										className="no-round no-border"
										value={item.print_name || ''}
										placeholder={"Print Name"}
										onChange={(e: any) => change(i, "print_name", e.target.value)}
									/>
								</div>
							</td>
							{!showVariablePrice && !usingPoints && (
								<td className="border-white-20">
									<Input
										type="number"
										min={0}
										step={0.01}
										className="no-round no-border"
										placeholder={currencySymbol}
										value={standardDisplayPrice as string}
										onWheel={(e) => e.currentTarget.blur()}
										onChange={(e: any) => {
											change(i, "price", e.target.value)
										}}
									/>
								</td>
							)}

							{!showVariablePrice && usingPoints && (<>
								<td className="border-white-20">
									<Input
										type="number"
										min={0.5}
										max={100}
										step={0.01}
										onWheel={(e) => e.currentTarget.blur()}
										className="no-round no-border"
										placeholder={currencySymbol}
										value={pointPrice?.whole?.points ?? ""}
										onChange={(e: any) => {
											const newValue = {
												whole: {
													points: e.target.value ? Number(e.target.value) : e.target.value
												}
											};
											updatePointPrice(newValue);
										}}
									/>
								</td>
								<td className="border-white-20">
									<Input
										type="number"
										min={0}
										max={100}
										step={0.01}
										onWheel={(e) => e.currentTarget.blur()}
										className="no-round no-border"
										placeholder={currencySymbol}
										value={pointPrice?.whole?.single ?? ""}
										onChange={(e: any) => {
											const newValue = {
												whole: {
													single: e.target.value ? Number(e.target.value) : e.target.value
												}
											};
											updatePointPrice(newValue);
										}}
									/>
								</td>
								<td className="border-white-20">
									<Input
										type="number"
										min={0}
										max={100}
										step={0.01}
										className="no-round no-border"
										onWheel={(e) => e.currentTarget.blur()}
										placeholder={currencySymbol}
										value={pointPrice?.whole?.double ?? ""}
										onChange={(e: any) => {
											const newValue = {
												whole: {
													double: e.target.value ? Number(e.target.value) : e.target.value
												}
											};
											updatePointPrice(newValue);
										}}
									/>
								</td>
								<td className="border-white-20">
									<Input
										type="number"
										min={0.5}
										max={100}
										step={0.01}
										onWheel={(e) => e.currentTarget.blur()}
										className="no-round no-border"
										placeholder={""}
										value={pointPrice?.half?.points ?? ""}
										onChange={(e: any) => {
											const newValue = {
												half: {
													points: e.target.value ? Number(e.target.value) : e.target.value
												}
											};
											updatePointPrice(newValue);
										}}
									/>
								</td>
								<td className="border-white-20">
									<Input
										type="number"
										min={0}
										max={100}
										step={0.01}
										onWheel={(e) => e.currentTarget.blur()}
										className="no-round no-border"
										placeholder={currencySymbol}
										value={pointPrice?.half?.single ?? ""}
										onChange={(e: any) => {
											const newValue = {
												half: {
													single: e.target.value ? Number(e.target.value) : e.target.value
												}
											};
											updatePointPrice(newValue);
										}}
									/>
								</td>
								<td className="border-white-20">
									<Input
										type="number"
										min={0}
										max={100}
										step={0.01}
										onWheel={(e) => e.currentTarget.blur()}
										className="no-round no-border"
										placeholder={currencySymbol}
										value={pointPrice?.half?.double ?? ""}
										onChange={(e: any) => {
											const newValue = {
												half: {
													double: e.target.value ? Number(e.target.value) : e.target.value
												}
											};
											updatePointPrice(newValue);
										}}
									/>
								</td>
							</>)}

							{showVariablePrice && !usingPoints &&
								variablePriceOptionSet!.options.map((o) => (
									<td key={`no-point-${o._id}-tdata`} className="border-white-20">
										<Input
											type="number"
											min={0}
											step={0.01}
											onWheel={(e) => e.currentTarget.blur()}
											className="no-round no-border"
											placeholder={currencySymbol}
											value={(item.prices && item.prices[o._id] ? item.prices[o._id] : "") as string}
											onChange={(e: any) => {
												const variable_prices = item.prices || {};
												variable_prices[o._id] = e.target.value;
												change(i, "prices", variable_prices);
											}}
										/>
									</td>
								))}

							{showVariablePrice && usingPoints && variablePriceOptionSet && variablePriceOptionSet.options &&
								variablePriceOptionSet!.options.map((o, j) => (<Fragment key={`with-point-${o._id}-tdata`}>
									<td className="border-white-20" style={o._id === selectedOptionId ? undefined : { display: "none" }}>
										<Input
											type={o._id === selectedOptionId ? "number" : "hidden"}
											min={0.5}
											max={100}
											step={0.01}
											onWheel={(e) => e.currentTarget.blur()}
											className="no-round no-border"
											placeholder={""}
											value={item.prices && typeof item.prices[o._id] === 'object' && 
											(item.prices[o._id] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices)?.whole && 
											(item.prices[o._id] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices).whole?.points ?
											(item.prices[o._id] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices).whole.points : ""}
											onChange={(e: any) => {
												const newValue = {
													[o._id]: {
														whole: {
															points: e.target.value ? Number(e.target.value) : e.target.value
														}
													}
												};
												const variable_prices = item.prices || {};
												_.merge(variable_prices, newValue);
												change(i, "prices", variable_prices);
											}}
										/>
									</td>
									<td className="border-white-20" style={o._id === selectedOptionId ? undefined : { display: "none" }}>
										<Input
											type={o._id === selectedOptionId ? "number" : "hidden"}
											min={0}
											max={100}
											step={0.01}
											onWheel={(e) => e.currentTarget.blur()}
											className="no-round no-border"
											placeholder={currencySymbol}
											value={item.prices && typeof item.prices[o._id] === 'object' && 
											(item.prices[o._id] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices)?.whole && 
											(item.prices[o._id] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices).whole?.single ?
											(item.prices[o._id] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices).whole.single : ""}
											onChange={(e: any) => {
												const newValue = {
													[o._id]: {
														whole: {
															single: e.target.value ? Number(e.target.value) : e.target.value
														}
													}
												};
												const variable_prices = item.prices || {};
												_.merge(variable_prices, newValue);
												change(i, "prices", variable_prices);
											}}
										/>
									</td>
									<td className="border-white-20" style={o._id === selectedOptionId ? undefined : { display: "none" }}>
										<Input
											type={o._id === selectedOptionId ? "number" : "hidden"}
											min={0}
											max={100}
											step={0.01}
											onWheel={(e) => e.currentTarget.blur()}
											className="no-round no-border"
											placeholder={currencySymbol}
											value={item.prices && typeof item.prices[o._id] === 'object' && 
											(item.prices[o._id] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices)?.whole &&
											(item.prices[o._id] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices).whole?.double ?
											(item.prices[o._id] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices).whole.double : ""}
											onChange={(e: any) => {
												const newValue = {
													[o._id]: {
														whole: {
															double: e.target.value ? Number(e.target.value) : e.target.value
														}
													}
												};
												const variable_prices = item.prices || {};
												_.merge(variable_prices, newValue);
												change(i, "prices", variable_prices);
											}}
										/>
									</td>
									<td className="border-white-20" style={o._id === selectedOptionId ? undefined : { display: "none" }}>
										<Input
											type={o._id === selectedOptionId ? "number" : "hidden"}
											min={0.5}
											max={100}
											step={0.01}
											onWheel={(e) => e.currentTarget.blur()}
											className="no-round no-border"
											placeholder={""}
											value={item.prices && typeof item.prices[o._id] === 'object' && 
											(item.prices[o._id] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices)?.half &&
											(item.prices[o._id] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices).half?.points ? 
											(item.prices[o._id] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices).half.points : ""}
											onChange={(e: any) => {
												const newValue = {
													[o._id]: {
														half: {
															points: e.target.value ? Number(e.target.value) : e.target.value
														}
													}
												};
												const variable_prices = item.prices || {};
												_.merge(variable_prices, newValue);
												change(i, "prices", variable_prices);
											}}
										/>
									</td>
									<td className="border-white-20" style={o._id === selectedOptionId ? undefined : { display: "none" }}>
										<Input
											type={o._id === selectedOptionId ? "number" : "hidden"}
											min={0}
											max={100}
											step={0.01}
											onWheel={(e) => e.currentTarget.blur()}
											className="no-round no-border"
											placeholder={currencySymbol}
											value={item.prices && typeof item.prices[o._id] === 'object' && 
											(item.prices[o._id] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices)?.half &&
											(item.prices[o._id] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices).half?.single ?
											(item.prices[o._id] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices).half.single : ""}
											onChange={(e: any) => {
												const newValue = {
													[o._id]: {
														half: {
															single: e.target.value ? Number(e.target.value) : e.target.value
														}
													}
												};
												const variable_prices = item.prices || {};
												_.merge(variable_prices, newValue);
												change(i, "prices", variable_prices);
											}}
										/>
									</td>
									<td className="border-white-20" style={o._id === selectedOptionId ? undefined : { display: "none" }}>
										<Input
											type={o._id === selectedOptionId ? "number" : "hidden"}
											min={0}
											max={100}
											step={0.01}
											onWheel={(e) => e.currentTarget.blur()}
											className="no-round no-border"
											placeholder={currencySymbol}
											value={item.prices && typeof item.prices[o._id] === 'object' && 
											(item.prices[o._id] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices)?.half &&
											(item.prices[o._id] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices).half?.double ?
											(item.prices[o._id] as T.Schema.Restaurant.Menu.RestaurantOptionSetOptionPointPrices).half.double : ""}
											onChange={(e: any) => {
												const newValue = {
													[o._id]: {
														half: {
															double: e.target.value ? Number(e.target.value) : e.target.value
														}
													}
												};
												const variable_prices = item.prices || {};
												_.merge(variable_prices, newValue);
												change(i, "prices", variable_prices);
											}}
										/>
									</td>
								</Fragment>
								))}

							<td className="border-white-20" style={{ display: "none" }}>
								<Input
									type="number"
									min={0}
									step={1}
									className="no-round no-border"
									placeholder={""}
									value={item.status === "not-available" ? "" : stock}
									disabled={item.status === "not-available"}
									onChange={(e: any) => {
										let value: "" | number;
										try {
											value =
												e.target.value === "" ? "" : parseInt(e.target.value);
										} catch (e) {
											console.log(e);
											return;
										}

										const rs = cloneDeepSafe(restaurantStock);

										if (value === "") {
											delete rs.option_set_options[item._id];
										} else {
											rs.option_set_options[item._id] = value;
										}

										setRestaurantStock(rs);
									}}
								/>
							</td>

							<td className="border-white-20 p-lr-2">
								<Checkbox
									id={`${i}-unavailable`}
									checked={item.status === "not-available"}
									onChange={() => {
										change(
											i,
											"status",
											item.status === "not-available" ? null : "not-available"
										);
										if (item.quantity === 1) {
											setDefault(i, 0);
										}
									}}
								/>
							</td>

							{defaultEnabled && (
								<td className="border-white-20 p-lr-2">
									<Checkbox
										id={`${i}-defaultos`}
										checked={item.quantity === 1}
										onChange={() => setDefault(i, item.quantity === 1 ? 0 : 1)}
									/>
								</td>
							)}

							{wpos_status && (
								<td className="border-white-20">
									<div className="flex-center">
										<Input
											type="number"
											className="no-round no-border"
											value={item.integrations?.pushpos?.item_id || ''} // Safely access item_id
											placeholder={"POS Item Id"}
											required={false}
											onChange={(e: any) => {
												const updatedValue = parseInt(e.target.value, 10);

												// Ensure pushpos exists before updating
												const updatedIntegrations = {
													...item.integrations,
													pushpos: {
														...item.integrations?.pushpos,
														item_id: updatedValue,
													},
												};

												change(i, "integrations", updatedIntegrations); // Pass the updated integrations object
											}}
										/>
									</div>
								</td>
							)}

							<td
								className="border-white-20 cursor text-center"
								onClick={() => remove(i)}
							>
								<Tooltip
									text="Delete"
									width={65}
									position="top"
								>
									<FaMinusCircle />
								</Tooltip>
							</td>
							<td
								className="border-white-20 cursor text-center"
								onClick={() => copy(i)}
							>
								<Tooltip
									text="Copy"
									width={55}
									position="top"
								>
									<FaCopy />
								</Tooltip>
							</td>
						</tr>
					);

					if (sortDirection) {
						return row(null, null, undefined);
					}

					return (
						<Draggable key={item._id} draggableId={item._id} index={i}>
							{(provided) =>
								row(
									provided.dragHandleProps as React.HTMLAttributes<HTMLElement>,
									provided.innerRef,
									provided.draggableProps
								)
							}
						</Draggable>
					);
				})}
			</>
		);
	}
}

@inject("store")
@observer
export class FieldOptionSetOptions extends MobxComponent<
	Props,
	{
		priceView: string;
		selectedOptionId: string;
		sortDirection: MenuListSortDirection;
	}
> {
	constructor(props: Props) {
		super(props);
		autobind(this);
		this.state = {
			priceView: props.values.variable_price_ref ? "variable" : "standard",
			selectedOptionId: this.getFirstVariableOptionSetOptionId(props),
			sortDirection: null,
		};
	}

	remove: HandleByIndex = (i) => {
		const values = [...this.props.values.options];
		values.splice(i, 1);
		this.props.setFieldValue("options", values);
	};

	copy: HandleByIndex = (i) => {
		const values = cloneDeepSafe(this.props.values.options);

		const newValue = {
			...JSON.parse(JSON.stringify(values[i])),
			_id: shortid.generate(),
			name: "",
			quantity: 0,
		};

		values.splice(i + 1, 0, newValue);
		this.props.setFieldValue("options", values);
	}

	change: HandleChange = (i, key, value) => {
		const values = [...this.props.values.options];
		// @ts-ignore
		values[i][key] = value;
		this.props.setFieldValue("options", values);
	};
	// Sorting reorders the form's options (persisted when the option set is saved);
	// the direction is kept only to show the active icon and pause drag-and-drop.
	onOptionSortDirectionChange = (direction: MenuListSortDirection) => {
		const { values, setFieldValue } = this.props;
		if (direction) {
			setFieldValue("options", sortByName([...values.options], direction));
		}
		this.setState({ sortDirection: direction });
	};
	onOptionDragEnd = (result: DropResult) => {
		if (this.state.sortDirection || !result.destination) {
			return;
		}
		const values = [...this.props.values.options];
		this.props.setFieldValue(
			"options",
			arrayMove(values, result.source.index, result.destination.index)
		);
	};

	setDefault: HandleSetDefault = (i, quantity) => {
		this.props.setFieldValue(
			"options",
			this.props.values.options.map((item, index) => ({
				...item,
				quantity: i === index ? quantity : 0,
				status: i === index ? null : item.status,
			}))
		);
	};

	componentDidUpdate(prevProps: Readonly<Props>): void {
		if (
			this.props.values.variable_price_ref &&
			this.props.values.variable_price_ref !==
			prevProps.values.variable_price_ref
		) {
			const values = cloneDeepSafe(this.props.values.options);
			for (let i = 0; i < values.length; i++) {
				values[i].prices = {};
			}
			this.props.setFieldValue("options", values);
		}
	}

	getFirstVariableOptionSetOptionId(props: Readonly<Props>): string {
		const variablePriceOptionSet = !props.values.variable_price_ref ? null : this.props.option_sets.find((os) => os._id === props.values.variable_price_ref) || null;

		return variablePriceOptionSet && variablePriceOptionSet.options && variablePriceOptionSet.options.length > 0 ? variablePriceOptionSet.options[0]._id : "";
	}

	formatDecimalPlaces(value: number) {
		return value == 0 ? value : Number(value.toFixed(2))
	}

	autoCalculate(): void {
		// get all option set options (#main, #variable);
		// loop main and using whole points and whole single values, autocalculate the rest
		// subloop #variable and do the same
		// NOTE: I can't do type checking here :(
		const { priceView } = this.state;
		const { values, option_sets, setFieldValue } = this.props;
		const { variable_price_ref, using_points } = values;
		const effectiveUsingPoints = !!values.using_points;

		const variablePriceOptionSet = !variable_price_ref ? null : option_sets.find((os) => os._id === variable_price_ref) || null;
		const showVariablePrice = priceView === "variable" && !!variablePriceOptionSet;

		if (effectiveUsingPoints && values.options) {
			const optionSetOptions = values.options;
			const newOptionSetOptions = optionSetOptions.map((option) => {
				if (!showVariablePrice && option.price) {
					console.log(`AUTO CALCULATING STANDARD PRICING`);
					// calculate points
					const whole_points = typeof option.price === 'object' && option.price.whole && option.price.whole.points ? option.price.whole.points : 0;
					const half_points = whole_points / 2;
					// calculate prices
					const whole_single_value = this.formatDecimalPlaces(typeof option.price === 'object' && option.price.whole && option.price.whole.single ? option.price.whole.single : 0);
					const whole_double_value = this.formatDecimalPlaces(whole_single_value * 2);
					const half_single_value = this.formatDecimalPlaces(whole_single_value / 2);
					const half_double_value = this.formatDecimalPlaces(whole_single_value);

					const newOptionValue = {
						whole: {
							points: whole_points,
							single: whole_single_value,
							double: whole_double_value
						},
						half: {
							points: half_points,
							single: half_single_value,
							double: half_double_value
						}
					};
					option.price = newOptionValue;
				} else if (showVariablePrice && option.prices) {
					console.log(`AUTO CALCULATING VARIABLE PRICING`);
					// TODO: autocalculate variable pricing
					const newOptionPrices = option.prices;
					_.forIn(newOptionPrices, (price, variableOptionSetId) => {

						// calculate points
						const whole_points = typeof price === 'object' && price.whole && price.whole.points ? price.whole.points : 0;
						const half_points = whole_points / 2;
						// calculate prices
						const whole_single_value = this.formatDecimalPlaces(typeof price === 'object' && price.whole && price.whole.single ? price.whole.single : 0);
						const whole_double_value = this.formatDecimalPlaces(whole_single_value * 2);
						const half_single_value = this.formatDecimalPlaces(whole_single_value / 2);
						const half_double_value = this.formatDecimalPlaces(whole_single_value);

						const newOptionValue = {
							whole: {
								points: whole_points,
								single: whole_single_value,
								double: whole_double_value
							},
							half: {
								points: half_points,
								single: half_single_value,
								double: half_double_value
							}
						};
						newOptionPrices[variableOptionSetId] = newOptionValue;
					});
					option.prices = newOptionPrices;
				}
				return option;
			});
			setFieldValue("options", newOptionSetOptions);
		}
	}

	render() {
		const { priceView, selectedOptionId, sortDirection } = this.state;
		const { values, defaultEnabled, option_sets, setFieldValue } = this.props;
		const displayOptions = values.options;
		const { variable_price_ref } = values;
		const using_points = !!values.using_points;

		const currencySymbol = this.injected.store.restaurant?.settings.region.currency.symbol || "$";		

		const variablePriceOptionSet = !variable_price_ref ? null : option_sets.find((os) => os._id === variable_price_ref) || null;
		const showVariablePrice = priceView === "variable" && !!variablePriceOptionSet;

		wpos_status = !!this.injected.store.restaurant?.wpos?.wpos_status;

		const sortVariablePriceOptions = (options: T.Schema.Restaurant.Menu.RestaurantOptionSetOption[]) => {
			const typeName = [
			  options.filter((os) => os.name.length < 14),
			  options.filter((os) => os.name.length >= 14 && os.name.length < 23),
			  options.filter((os) => os.name.length >= 23)
			];
		  
			const sortedOptions = [];
		  
			typeName[2].forEach((item) => sortedOptions.push([item]));
		  
			while (typeName[1].length > 0) {
			  const group = [typeName[1].shift()];
			  if (typeName[1].length > 0) group.push(typeName[1].shift());
			  sortedOptions.push(group);
			}
		  
			while (typeName[0].length >= 3) {
			  sortedOptions.push([typeName[0].shift(), typeName[0].shift(), typeName[0].shift()]);
			}
		  
			while (typeName[0].length > 0) {
			  const group = [typeName[0].shift()];
			  if (typeName[0].length > 0) group.push(typeName[0].shift());
			  sortedOptions.push(group);
			}
		  
			return sortedOptions.flat();
		  };

		return (
			<>
				<div className="flex-line flex-center-v flex-wrap m-b-3">

					<Select
						style={{ width: "auto", height: "30px" }}
						className="flex-grow"
						value={values.variable_price_ref}
						onChange={(e) => {
							if (e.target.value === "") {
								this.setState({ priceView: "standard" });
							}
							setFieldValue("variable_price_ref", e.target.value);
						}}
						options={[
							{ value: "", label: "No Variable Price" },
							...option_sets
								.filter((os) => {
									return (
										os._id !== values._id &&
										!os.conditions.multi_select &&
										!os.conditions.quantity_select
									);
								})
								.map((os) => ({ value: os._id, label: os.name })),
						]}
					/>

					{values.variable_price_ref && (
						<ButtonGroup
							size="xs"
							width={100}
							className="m-l-1"
							selected={priceView}
							onSelect={(v) => this.setState({ priceView: v.value as string })}
							options={[
								{ value: "standard", name: `Regular (${currencySymbol})` },
								{ value: "variable", name: `Variable (${currencySymbol})` },
							]}
						/>
					)}

				</div>

				<div className="flex-line flex-center-v flex-center-h flex-wrap m-b-3">
					<Button
						size="xs"
						type="button"
						color="primary-inverse"
						className="m-r-1"
						onClick={() => {
							setFieldValue("options", [
								...values.options,
								{
									_id: shortid.generate(),
									name: "",
									price: "",
									quantity: 0,
									prices: {},
								} as T.Schema.Restaurant.Menu.RestaurantOptionSetOption,
							]);
						}}
					>
						Add Option
					</Button>

					{using_points && (
						<Button
							size="xs"
							type="button"
							color="primary-inverse"
							className="m-l-1"
							onClick={() => { this.autoCalculate() }}
						>
							Auto Calculate
						</Button>
					)}
				</div>

				<div className="flex-line flex-center-v flex-center-h flex-wrap m-b-3">
					{values.variable_price_ref && priceView === "variable" && variablePriceOptionSet && variablePriceOptionSet.options && using_points && (
						<ButtonGroup
							size="xs"
							width={160}
							className="m-l-1"
							buttonClassName="p-lr-2"
							selected={selectedOptionId}
							onSelect={(v) => this.setState({ selectedOptionId: v.value as string })}
							options={
								variablePriceOptionSet &&  sortVariablePriceOptions(variablePriceOptionSet.options)
									.map((os: any) => ({ value: os._id, name: os.name }))
							}
							buttonWidth
					/>
					)}
				</div>

				<DragDropContext onDragEnd={this.onOptionDragEnd}>
				<Table className="width100">
					<thead>
						<tr className="text-center">
							<th
								{...rowDataProps}
								style={{ minWidth: "20px" }}
							/>
							<th {...rowDataProps}>
								<div
									style={{
										alignItems: "center",
										display: "inline-flex",
										gap: 8,
										justifyContent: "center",
									}}
								>
									Name
									{values.options.length > 1 && (
										<MenuSortDirectionButtons
											compact
											sortDirection={sortDirection}
											onSortDirectionChange={this.onOptionSortDirectionChange}
										/>
									)}
								</div>
							</th>
							<th {...rowDataProps}>Print Name</th>
							{!showVariablePrice && !using_points && <th {...rowDataProps}>Price ({currencySymbol})</th>}
							{showVariablePrice && !using_points &&
								variablePriceOptionSet!.options.map((o, i) => (
									<th
										key={`no-points-theader-${o._id}`}
										{...rowDataProps}
										style={{ whiteSpace: "normal" }}
									>
										{o.name} ({currencySymbol})
									</th>
								))}

							{!showVariablePrice && using_points && (<>
								<th {...rowDataProps}><WholeIcon /> Points</th>
								<th {...rowDataProps}><WholeIcon /> x1</th>
								<th {...rowDataProps}><WholeIcon /> x2</th>
								<th {...rowDataProps}><HalfIcon /> Points</th>
								<th {...rowDataProps}><HalfIcon /> x1</th>
								<th {...rowDataProps}><HalfIcon /> x2</th>
							</>)}

							{showVariablePrice && using_points &&
								variablePriceOptionSet!.options.map((o, i) => (<Fragment key={`with-points-theader-${o._id}-${i}`}>
									<th {...rowDataProps} style={o._id === selectedOptionId ? undefined : { display: "none" }}><WholeIcon /> Points</th>
									<th {...rowDataProps} style={o._id === selectedOptionId ? undefined : { display: "none" }}><WholeIcon /> x1</th>
									<th {...rowDataProps} style={o._id === selectedOptionId ? undefined : { display: "none" }}><WholeIcon /> x2</th>
									<th {...rowDataProps} style={o._id === selectedOptionId ? undefined : { display: "none" }}><HalfIcon /> Points</th>
									<th {...rowDataProps} style={o._id === selectedOptionId ? undefined : { display: "none" }}><HalfIcon /> x1</th>
									<th {...rowDataProps} style={o._id === selectedOptionId ? undefined : { display: "none" }}><HalfIcon /> x2</th>
								</Fragment>
								))}
							<th {...rowDataProps} style={{ display: "none" }}># in Stock</th>
							<th {...rowDataProps} style={{ maxWidth: "50px", whiteSpace: "normal" }}>No Stock</th>
							{defaultEnabled && <th {...rowDataProps} style={{ maxWidth: "60px" }}>Default</th>}
							{wpos_status && <th {...rowDataProps}>POS Item Id</th>}
							<th {...rowDataProps} style={{ minWidth: "20px" }} />
							<th {...rowDataProps} style={{ minWidth: "20px" }} />
						</tr>
					</thead>
					<Droppable droppableId="option-set-options">
						{(provided) => (
							<tbody
								ref={provided.innerRef}
								{...provided.droppableProps}
							>
								<TableData
									items={displayOptions}
									sourceOptions={values.options}
									sortDirection={sortDirection}
									change={this.change}
									remove={this.remove}
									copy={this.copy}
									setDefault={this.setDefault}
									defaultEnabled={defaultEnabled}
									showVariablePrice={showVariablePrice}
									variablePriceOptionSet={variablePriceOptionSet}
									restaurantStock={this.props.restaurantStock}
									setRestaurantStock={this.props.setRestaurantStock}
									usingPoints={using_points}
									selectedOptionId={selectedOptionId}
									currencySymbol={currencySymbol}
								/>
								{provided.placeholder}
							</tbody>
						)}
					</Droppable>
				</Table>
				</DragDropContext>
			</>
		);
	}
}